import { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { format } from 'date-fns';

import { httpStatusCodes } from '@common/Constants';
import { isBool, isObject } from '@common/helpers/helpers';
import Logger from '@common/Logger.js';
import { CUSTOMER_LOYALTY_EVENTS, SEARCH_DELAY_TIME } from '@common/network';
import { dateInitFormats, getDevice, setLocalStorage, getLocalStorage } from '@common/Util';
import useDebounce from '@hooks/Debounce/useDebounce';
import * as routerConstants from '@navigation/Constants.js';
import { getEventFiltersConfig } from '@pages/Catalog/config';
import { addStandardPrice } from '@pages/EventDetails/Util';
import { getByPathAndParams } from '@services/BaseApi';

const initSearchParams = {
  areMyEvents: false,
  sortBy: '',
  sortDirection: '',
  search: '',
  page: 0,
  filters: ''
};

const initTEMParams = {
  eventTypeKey: {},
  eventTopicKey: {},
  groupTypeKey: {},
  groupTopicKey: {},
  eventLanguage: {},
  eventsPrices: [0, 100],
  locations: {},
  city: {},
  country: {},
  dateRange: {
    from: '',
    to: ''
  }
};

const formatFilterForBE = (filterParams, filtersOptions) => {
  const filtersArr = [];
  Object.entries(filterParams).forEach(([key, value]) => {
    if (value.length) {
      const [minValue, maxValue] = value;
      if (
        key === 'eventsPrices' &&
        (filtersOptions?.eventsPrices.minPrice !== minValue ||
          filtersOptions?.eventsPrices.maxPrice !== maxValue)
      ) {
        if (minValue === 0 || maxValue === 0) {
          filtersArr.push({ key: 'price', operation: 'is_null' });
        }

        if (minValue === maxValue) {
          filtersArr.push({ key: 'price', operation: 'equal', value: minValue });
        } else if (minValue !== 0 || maxValue !== 0) {
          filtersArr.push(
            { key: 'price', operation: 'greater_than_equal', value: minValue },
            { key: 'price', operation: 'less_than_equal', value: maxValue }
          );
        }
      }
    } else {
      Object.entries(value).forEach(([innerKey, innerValue]) => {
        if (key === 'dateRange') {
          if (innerKey === 'from' && innerValue != '') {
            filtersArr.push({
              key: 'startdate',
              operation: 'is_on_or_after',
              value: format(innerValue, dateInitFormats.serverResponseDateFormat)
            });
          } else if (innerKey === 'to' && innerValue != '') {
            filtersArr.push({
              key: 'startdate',
              operation: 'is_on_or_before',
              value: format(innerValue, dateInitFormats.serverResponseDateFormat)
            });
          }
        } else {
          if (innerValue) {
            filtersArr.push({ key, operation: 'equal', value: innerKey });
          }
        }
      });
    }
  });

  return filtersArr;
};

const useFilterKey = [
  'eventsTopics',
  'eventsGroupsTopics',
  'eventsGroupsTypes',
  'eventsTypes',
  'eventsLocations'
];

const modifyElements = (obj, keys) => {
  keys.forEach((key) => {
    if (Array.isArray(obj[key])) {
      obj[key] = obj[key].map((item) => {
        if (item.key) {
          item.realValue = item.key;
        } else {
          item.realValue = item.value;
          item.key = item.id;
        }
        return item;
      });
    }
  });
};

const clearAllValues = (obj) => {
  for (let key in obj) {
    if (isObject(obj[key])) {
      clearAllValues(obj[key]);
    } else if (isBool(obj[key])) {
      obj[key] = false;
    } else {
      obj[key] = '';
    }
  }
};

const useCatalogLogic = (isOwnEvents = false, contextData, isInfinityList, configuration) => {
  let navigate = useNavigate();

  const loggedIn = contextData?.loggedIn;

  const [searchParamsURL] = useSearchParams();
  const [applySearchParamsFilters, setApplySearchParamsFilters] = useState(!!searchParamsURL?.size);

  const [events, setEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [searchParams, setSearchParams] = useState({
    ...initSearchParams,
    areMyEvents: Boolean(isOwnEvents),
    userEmail: loggedIn ? 'tobias.rothley@contrimo.com' : null
  });

  const [filterParams, setFilterParams] = useState(initTEMParams);
  const [filtersOptions, setFiltersOptions] = useState([]);
  const [isFilterOn, setIsFilterOn] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [sortIcon, setSortIcon] = useState('sort');
  const [showFilters, setShowFilters] = useState(false);
  const [catalogView, setCatalogView] = useState('grid');

  const [availableFilterOptions] = useState(getEventFiltersConfig());

  const onSearchFieldChange = ({ target }) => {
    setSearchParams((prevState) => ({
      ...prevState,
      search: target?.value
    }));
  };

  const onItemClick = (event) => {
    navigate(routerConstants.routes.name.eventDetails + '/' + event?.uuid, {
      state: {
        from: loggedIn ? Localize.get('AppNavBar.catalog') : Localize.get('AppNavBar.eventCatalog'),
        path: routerConstants.routes.name.catalog,
        data: event?.type?.value,
        thumbnailImage: event?.thumbnailImage
      }
    });
  };

  const onFilterChange = (e, filter) => {
    switch (e.target.type) {
      case 'checkbox':
        setFilterParams((prevState) => ({
          ...prevState,
          [filter]: { ...prevState[filter], [e.target.name]: e.target.checked }
        }));

        break;
      case 'calendar-range':
        setFilterParams((prevState) => ({
          ...prevState,
          dateRange: {
            ...prevState.dateRange,
            [filter.range]: filter?.date ? filter.date : ''
          }
        }));
        break;
      case 'slider':
        setFilterParams((prevState) => ({
          ...prevState,
          [filter]: e.target.value
        }));

        break;
      default:
        break;
    }
  };

  const onResetFilters = () => {
    let paramsWithValue = structuredClone(filterParams);
    clearAllValues(paramsWithValue);
    setFilterParams({
      ...paramsWithValue,
      eventsPrices: [filtersOptions?.eventsPrices?.minPrice, filtersOptions?.eventsPrices?.maxPrice]
    });
    setCurrentPage(1);
    setIsFilterOn(false);
    setSearchParams((prev) => ({ ...prev, filters: initSearchParams.filters, page: 0 }));
  };

  const onApplyFilters = async () => {
    const filtersArr = formatFilterForBE(filterParams, filtersOptions);
    setCurrentPage(1);
    setSearchParams((prev) => ({
      ...prev,
      filters: filtersArr.length ? { advancedFilters: filtersArr ?? [] } : '',
      page: 0
    }));
    if (filtersArr.length) {
      setIsFilterOn(true);
    } else {
      setIsFilterOn(false);
    }
  };

  const handleSorting = (name, value, iconName) => {
    const alreadySelected = name === searchParams.sortBy && value === searchParams.sortDirection;
    setSearchParams((prevState) => ({
      ...prevState,
      sortBy: alreadySelected ? '' : name,
      sortDirection: alreadySelected ? '' : value
    }));
    setSortIcon((prevName) => (prevName === iconName ? 'sort' : iconName));
  };

  const getFiltersOptions = async () => {
    setIsFilterLoading(true);
    try {
      const { data } = await getByPathAndParams({
        path: CUSTOMER_LOYALTY_EVENTS.GET_CUSTOMER_EVENTS_FILTERS,
        params: loggedIn
          ? { areMyEvents: Boolean(isOwnEvents), userEmail: 'tobias.rothley@contrimo.com' }
          : { areMyEvents: false },
        temApi: true
      });

      modifyElements(data, useFilterKey);
      setFiltersOptions(data);
      setFilterParams((prevState) => ({
        ...prevState,
        eventsPrices: [data?.eventsPrices?.minPrice, data?.eventsPrices?.maxPrice]
      }));
      return data;
    } catch (err) {
      setFiltersOptions([]);
    } finally {
      setIsFilterLoading(false);
    }
  };

  const fetchEvents = async (params) => {
    try {
      const { data, status } = await getByPathAndParams({
        path: CUSTOMER_LOYALTY_EVENTS.GET_CUSTOMER_EVENTS,
        params,
        temApi: true
      });

      if (status === httpStatusCodes.ok) {
        setTotalPages(data?.totalPages);
        return data?.content ?? [];
      }
    } catch (err) {
      Logger.error(err);
    }
    return [];
  };

  const fetchAllEvents = async () => {
    setIsLoading(true);
    let eventsData = [];

    if (filterParams.dateRange.from != '' && filterParams.dateRange.to != '') {
      const searchParamsForFirstCall = structuredClone(searchParams);

      searchParamsForFirstCall.filters.advancedFilters =
        searchParamsForFirstCall.filters.advancedFilters.filter(
          (filter) => filter.operation !== 'is_on_or_before'
        );
      const eventsAfterDate = await fetchEvents(searchParamsForFirstCall);

      const searchParamsForSecondCall = structuredClone(searchParams);
      searchParamsForSecondCall.filters.advancedFilters =
        searchParams.filters.advancedFilters.filter(
          (filter) => filter.operation !== 'is_on_or_after'
        );
      const eventsBeforeDate = await fetchEvents(searchParamsForSecondCall);

      eventsData = eventsAfterDate.filter((eventAfter) =>
        eventsBeforeDate.some((eventBefore) => eventBefore.id === eventAfter.id)
      );
    } else {
      eventsData = await fetchEvents(searchParams);
    }

    if (isInfinityList) {
      setEvents((prevState) => [
        ...prevState,
        ...eventsData.map((event) => addStandardPrice(event))
      ]);
    } else {
      setEvents(eventsData.map((event) => addStandardPrice(event)));
    }
    setIsLoading(false);
  };

  const readURLParams = async () => {
    setIsLoading(true);

    const filtersOptions = await getFiltersOptions();

    const validateSearchParamValue = (key, value, availableFilterOptions, filtersOptions) => {
      const filterKeyFound = availableFilterOptions.find((filter) => filter.name === key)?.options;
      const keyOrValue = useFilterKey.includes(filterKeyFound) ? 'key' : 'value';
      if (filterKeyFound) {
        return (
          filtersOptions[filterKeyFound].find(
            (filter) => filter?.key?.toLowerCase() === value?.toLowerCase()
          )?.[keyOrValue] || value
        );
      } else return '';
    };

    if (applySearchParamsFilters) {
      setApplySearchParamsFilters(false);
      const filters = {};
      searchParamsURL.forEach((value, key) => {
        const validValue = validateSearchParamValue(
          key,
          value,
          availableFilterOptions,
          filtersOptions
        );
        if (validValue) {
          filters[key] = { ...(filters[key] || {}), [validValue]: true };
        }
      });

      setFilterParams((prev) => ({
        ...prev,
        ...filters
      }));
      const filtersArr = formatFilterForBE(filters);
      setSearchParams((prev) => ({
        ...prev,
        filters: filtersArr.length ? { advancedFilters: filtersArr ?? [] } : null
      }));
    }
  };

  useEffect(() => {
    const device = getDevice(window.innerWidth).toLowerCase();
    setSearchParams((prevState) => ({
      ...prevState,
      size: configuration?.PAGINATION?.myAreaCustomerLoyaltyEventsLimit?.[catalogView]?.[device],
      page: 0
    }));

    setLocalStorage('catalogView', catalogView);
    if (setCurrentPage) {
      setCurrentPage(1);
    }
  }, [catalogView]);

  useEffect(() => {
    if (!applySearchParamsFilters && searchParams.size) {
      fetchAllEvents();
    }
  }, [
    searchParams.page,
    searchParams.sortBy,
    searchParams.sortDirection,
    searchParams.filters,
    searchParams.size
  ]);

  useEffect(() => {
    readURLParams();
    if (typeof window !== 'undefined') {
      const catalogViewLocalStorage = getLocalStorage('catalogView');
      setCatalogView(catalogViewLocalStorage ? catalogViewLocalStorage : 'grid');
    }
  }, []);

  useDebounce(
    () => {
      fetchAllEvents();
    },
    [searchParams.search],
    SEARCH_DELAY_TIME
  );

  return {
    isLoading,
    onApplyFilters,
    onResetFilters,
    onSearchFieldChange,
    events,
    totalPages,
    currentPage,
    setCurrentPage,
    availableFilterOptions,
    onItemClick,
    handleSorting,
    searchParams,
    onFilterChange,
    filterParams,
    filtersOptions,
    isFilterOn,
    isFilterLoading,
    setSearchParams,
    sortIcon,
    showFilters,
    setShowFilters,
    catalogView,
    setCatalogView,
    setIsLoading
  };
};

export default useCatalogLogic;
